/**
 * A custom element that applies a discount to the cart.
 */
class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.activeFetch = null;
    this.initialized = true;

    // Bind methods to preserve 'this' context
    this.applyDiscount = this.applyDiscount.bind(this);
    this.removeDiscount = this.removeDiscount.bind(this);

    // Add event listeners
    this.addEventListener("submit", this.applyDiscount);
    this.addEventListener("click", this.removeDiscount);

    this.querySelector(".cart-discount__title").addEventListener(
      "click",
      this.toggleDiscounts.bind(this)
    );
  }

  toggleDiscounts() {
    this.querySelector(".cart-discount__form-inner").classList.toggle("hidden");
  }

  closeDiscounts() {
    this.querySelector(".cart-discount__form-inner").classList.add("hidden");
    this.querySelector(".cart-discount__error").classList.add("hidden");
  }

  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }

    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount code to the cart.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  async applyDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.createAbortController();

    try {
      const existingDiscounts = this.existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      // Hide any existing error messages
      this.hideErrors();

      const response = await fetch(window.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(","),
          sections: this.getSectionIds(),
        }),
        signal: abortController.signal,
      });

      const data = await response.json();

      // Check if discount code is invalid
      if (
        data.discount_codes &&
        data.discount_codes.find((discount) => {
          return (
            discount.code === discountCodeValue && discount.applicable === false
          );
        })
      ) {
        discountCode.value = "";
        this.handleDiscountError("discount_code");
        return;
      }

      // Check for shipping discount conflicts
      if (data.sections) {
        const sectionIds = this.getSectionIds();
        for (const sectionId of sectionIds) {
          const newHtml = data.sections[sectionId];
          if (newHtml) {
            const parsedHtml = new DOMParser().parseFromString(
              newHtml,
              "text/html"
            );
            const section = parsedHtml.getElementById(
              `shopify-section-${sectionId}`
            );
            const discountCodes =
              section?.querySelectorAll(".cart-discount__pill") || [];

            if (section) {
              const codes = Array.from(discountCodes)
                .map((element) => element.dataset.discountCode)
                .filter(Boolean);

              // Check if shipping discount conflict exists
              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                data.discount_codes.find((discount) => {
                  return (
                    discount.code === discountCodeValue &&
                    discount.applicable === true
                  );
                })
              ) {
                this.handleDiscountError("shipping");
                discountCode.value = "";
                return;
              }
            }
          }
        }
      }

      // Refresh cart data to ensure all sections are up to date
      this.refreshCartData();

      // Clear the input on success
      discountCode.value = "";
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error applying discount:", error);
        this.handleDiscountError("discount_code");
      }
    } finally {
      this.activeFetch = null;
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  async removeDiscount(event) {
    // Only handle clicks on discount removal buttons
    if (!event.target.closest(".cart-discount__remove")) return;

    event.preventDefault();
    event.stopPropagation();

    const pill = event.target.closest(".cart-discount__pill");
    if (!pill) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.createAbortController();

    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: existingDiscounts.join(","),
          sections: this.getSectionIds(),
        }),
        signal: abortController.signal,
      });

      const data = await response.json();

      if (data.status === 422) {
        console.error("Error removing discount:", data);
        return;
      }

      // Refresh cart data to ensure all sections are up to date
      this.refreshCartData();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error removing discount:", error);
      }
    } finally {
      this.activeFetch = null;
    }
  }

  /**
   * Hides all error messages
   */
  hideErrors() {
    const errorElements = this.querySelectorAll(".cart-discount__error");
    errorElements.forEach((element) => element.classList.add("hidden"));
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  handleDiscountError(type) {
    const errorElement = this.querySelector(`.cart-discount__error--${type}`);
    if (errorElement) {
      errorElement.classList.remove("hidden");
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  existingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll(".cart-discount__pill");
    for (const pill of discountPills) {
      if (pill.dataset.discountCode) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }
    return discountCodes;
  }

  /**
   * Gets the sections that need to be updated, similar to cart.js
   * @returns {Array} Array of section objects with id, section, and selector
   */
  getSectionsToRender() {
    const sections = [];

    // Check for cart drawer section
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      sections.push({
        id: "CartDrawerContainer",
        section: "cart-drawer",
      });
    }

    return sections;
  }

  /**
   * Gets the section IDs that need to be updated (for backward compatibility)
   * @returns {string[]}
   */
  getSectionIds() {
    return this.getSectionsToRender().map((section) => section.section);
  }

  /**
   * Gets the inner HTML of a section from the response HTML
   * @param {string} html - The HTML response
   * @param {string} selector - The selector to find the content
   * @returns {string} The inner HTML
   */
  getSectionInnerHTML(html, selector) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const sectionElement = parsedHTML.querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : html;
  }

  /**
   * Refreshes cart data by fetching fresh content from the server
   * Similar to the onCartUpdate method in cart.js
   */
  refreshCartData() {
    const sections = this.getSectionsToRender();

    sections.forEach((section) => {
      const url = `${window.routes.cart_url}?section_id=${section.section}`;

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );

          const targetElement = document.getElementById(section.id);
          const sourceElement = html.querySelector(`#${section.id}`);

          console.log("targetElement", targetElement);
          console.log("sourceElement", html, sourceElement);

          if (targetElement && sourceElement) {
            targetElement.innerHTML = sourceElement.innerHTML;
          } else {
            // Fallback to replacing the entire section
            targetElement.replaceWith(sourceElement);
          }
        })
        .catch((error) => {
          console.error("Error refreshing cart data:", error);
        });
    });
  }

  /**
   * Morphs a section with new HTML content
   * @param {string} sectionId - The section ID to update
   * @param {string} html - The new HTML content
   */
  morphSection(sectionId, html) {
    const fragment = new DOMParser().parseFromString(html, "text/html");
    const existingElement = document.getElementById(
      `shopify-section-${sectionId}`
    );

    if (existingElement && fragment.body.firstChild) {
      // Replace the content
      existingElement.innerHTML = fragment.body.firstChild.innerHTML;

      // Re-initialize any custom elements in the new content
      this.initializeCustomElements(existingElement);
    }
  }

  /**
   * Re-initializes custom elements after DOM update
   * @param {HTMLElement} container - The container element
   */
  initializeCustomElements(container) {
    // Re-initialize cart discount components
    const cartDiscountComponents = container.querySelectorAll(
      "cart-discount-component"
    );
    cartDiscountComponents.forEach((component) => {
      if (!component.initialized) {
        // Re-bind event listeners
        component.addEventListener("submit", component.applyDiscount);
        component.addEventListener("click", component.removeDiscount);
        component.initialized = true;
      }
    });
  }
}

if (!customElements.get("cart-discount-component")) {
  customElements.define("cart-discount-component", CartDiscount);
}
